import React from 'react';
import { Navigation } from 'lucide-react';
import { Language, DistanceUnit } from '../../types';
import { themeMap } from '../../utils/calcTheme';
import { t } from '../../utils/i18n';
import StepperField from '../StepperField';
import { milesToKm } from '../../utils/calculator';

interface DistancePreset {
  id: string;
  name: string;
  shortName: string;
  distanceKm: number;
}

interface DistanceInputCardProps {
  language: Language;
  distanceKm: number;
  isMiles: boolean;
  distancePresets: DistancePreset[];
  distWholeKm: number;
  distMeters: number;
  distWholeMiles: number;
  distFeet: number;
  onDistanceChange: (newKm: number, presetId?: string) => void;
  adjustWholeKm: (delta: number) => void;
  adjustMeters: (delta: number) => void;
  adjustWholeMiles: (delta: number) => void;
  adjustFeet: (delta: number) => void;
  setExactFeet: (feet: number) => void;
  triggerHaptic?: (duration?: number) => void;
}

/**
 * 2. Afstand-invoerkaart (Input)
 * - Oppervlakte: bg-slate-50 border border-slate-100 rounded-2xl
 * - Icoon-container: bg-emerald-100 text-emerald-600
 * - Actieve preset: bg-emerald-500 text-white shadow-sm
 * - Inactieve presets: bg-slate-200 text-slate-600
 */
export default function DistanceInputCard({
  language,
  distanceKm,
  isMiles,
  distancePresets,
  distWholeKm,
  distMeters,
  distWholeMiles,
  distFeet,
  onDistanceChange,
  adjustWholeKm,
  adjustMeters,
  adjustWholeMiles,
  adjustFeet,
  setExactFeet,
  triggerHaptic = () => {},
}: DistanceInputCardProps) {
  const styles = themeMap.distance.inputCard;

  return (
    <div
      id="form-card-distance"
      className={`p-5 sm:p-6 transition-all space-y-4 ${styles.container}`}
    >
      <div className="flex items-center gap-2.5">
        <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${styles.iconContainer}`}>
          <Navigation className="w-4 h-4 stroke-[2.4]" />
        </div>
        <div>
          <span className="text-sm font-bold text-slate-900 dark:text-slate-100 block">
            {t(language, 'distanceCardTitle')} ({isMiles ? 'mi / ft' : 'km / m'})
          </span>
        </div>
      </div>

      {/* Stepper Columns */}
      {isMiles ? (
        <div className="space-y-2.5">
          <div className="flex items-center gap-3">
            <StepperField
              idPrefix="dist-mi"
              unit={t(language, 'unitMi')}
              value={distWholeMiles}
              onIncrement={() => adjustWholeMiles(1)}
              onDecrement={() => adjustWholeMiles(-1)}
              onChange={(val) => {
                const newTotalMiles = val + distFeet / 5280;
                onDistanceChange(milesToKm(newTotalMiles));
              }}
              min={0}
              max={300}
              colorScheme="neutral"
            />

            <StepperField
              idPrefix="dist-mi-ft"
              unit={t(language, 'unitFt')}
              value={distFeet}
              displayValue={distFeet.toString()}
              onIncrement={() => adjustFeet(500)}
              onDecrement={() => adjustFeet(-500)}
              onChange={(val) => {
                const cleanFeet = Math.min(5279, Math.max(0, val));
                const newTotalMiles = distWholeMiles + cleanFeet / 5280;
                onDistanceChange(milesToKm(newTotalMiles));
              }}
              min={0}
              max={5279}
              step={100}
              colorScheme="neutral"
            />
          </div>

          {/* Quick foot nudges & fractions */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5 pt-0.5 scrollbar-none">
            {[-500, -100, 100, 500].map((delta) => (
              <button
                key={delta}
                id={`btn-ft-nudge-${delta > 0 ? `plus-${delta}` : `minus-${Math.abs(delta)}`}`}
                type="button"
                onClick={() => {
                  triggerHaptic(8);
                  adjustFeet(delta);
                }}
                className="px-2.5 py-1 rounded-xl text-xs font-bold font-mono transition-all cursor-pointer select-none bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 active:scale-95 text-slate-700 dark:text-slate-300 whitespace-nowrap"
              >
                {delta > 0 ? `+${delta}` : delta} ft
              </button>
            ))}

            <div className="w-[1px] h-4 bg-black/10 dark:bg-white/10 shrink-0 mx-0.5" />

            {[
              { label: '0 ft', ft: 0 },
              { label: '¼ mi (1320 ft)', ft: 1320 },
              { label: '½ mi (2640 ft)', ft: 2640 },
              { label: '¾ mi (3960 ft)', ft: 3960 },
            ].map((fraction) => {
              const isMatch = Math.abs(distFeet - fraction.ft) < 40;
              return (
                <button
                  key={fraction.label}
                  id={`btn-ft-fraction-${fraction.ft}`}
                  type="button"
                  onClick={() => {
                    triggerHaptic(10);
                    setExactFeet(fraction.ft);
                  }}
                  className={`shrink-0 px-2.5 py-1 rounded-xl text-xs font-bold transition-all cursor-pointer select-none whitespace-nowrap ${
                    isMatch ? styles.activePreset : styles.inactivePreset
                  }`}
                >
                  {fraction.label}
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="space-y-2.5">
          <div className="flex items-center gap-3">
            <StepperField
              idPrefix="dist-km"
              unit={t(language, 'unitKm')}
              value={distWholeKm}
              onIncrement={() => adjustWholeKm(1)}
              onDecrement={() => adjustWholeKm(-1)}
              onChange={(val) => {
                const newTotal = val + distMeters / 1000;
                onDistanceChange(newTotal);
              }}
              min={0}
              max={500}
              colorScheme="neutral"
            />

            <StepperField
              idPrefix="dist-m"
              unit={t(language, 'unitM')}
              value={distMeters}
              onIncrement={() => adjustMeters(100)}
              onDecrement={() => adjustMeters(-100)}
              onChange={(val) => {
                const newTotal = distWholeKm + Math.min(999, Math.max(0, val)) / 1000;
                onDistanceChange(newTotal);
              }}
              min={0}
              max={999}
              step={50}
              colorScheme="neutral"
            />
          </div>

          {/* Quick meter nudges & fractions */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5 pt-0.5 scrollbar-none">
            {[-500, -100, 100, 500].map((delta) => (
              <button
                key={delta}
                id={`btn-m-nudge-${delta > 0 ? `plus-${delta}` : `minus-${Math.abs(delta)}`}`}
                type="button"
                onClick={() => {
                  triggerHaptic(8);
                  adjustMeters(delta);
                }}
                className="px-2.5 py-1 rounded-xl text-xs font-bold font-mono transition-all cursor-pointer select-none bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 active:scale-95 text-slate-700 dark:text-slate-300 whitespace-nowrap"
              >
                {delta > 0 ? `+${delta}` : delta} m
              </button>
            ))}

            <div className="w-[1px] h-4 bg-black/10 dark:bg-white/10 shrink-0 mx-0.5" />

            {[
              { label: '0 m', m: 0 },
              { label: '250 m', m: 250 },
              { label: '500 m', m: 500 },
              { label: '750 m', m: 750 },
            ].map((fraction) => {
              const isMatch = Math.abs(distMeters - fraction.m) < 25;
              return (
                <button
                  key={fraction.label}
                  id={`btn-m-fraction-${fraction.m}`}
                  type="button"
                  onClick={() => {
                    triggerHaptic(10);
                    const newTotal = distWholeKm + fraction.m / 1000;
                    onDistanceChange(newTotal);
                  }}
                  className={`shrink-0 px-2.5 py-1 rounded-xl text-xs font-bold transition-all cursor-pointer select-none whitespace-nowrap ${
                    isMatch ? styles.activePreset : styles.inactivePreset
                  }`}
                >
                  {fraction.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Presets directly under the distance inputs */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5 pt-0.5 scrollbar-none">
        {distancePresets.map((preset) => {
          const isSelected = Math.abs(distanceKm - preset.distanceKm) < 0.08;
          return (
            <button
              key={preset.id}
              id={`btn-preset-${preset.id}`}
              type="button"
              onClick={() => {
                triggerHaptic(10);
                onDistanceChange(preset.distanceKm, preset.id);
              }}
              className={`shrink-0 px-3 py-1.5 rounded-xl text-xs font-bold transition-all select-none cursor-pointer ${
                isSelected ? styles.activePreset : styles.inactivePreset
              }`}
            >
              {preset.shortName}
            </button>
          );
        })}
      </div>
    </div>
  );
}
